import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Sun, Shield, TreePine, ShoppingBag, Utensils, Mountain, GraduationCap, BookOpen, Trophy, Star, Heart, Car, Plane } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
import { SEOHead } from '../components/SEOHead';
import { RelatedCommunities } from '../components/RelatedCommunities';
const placeholderListings = [{
  id: 1,
  price: '$1,850,000',
  address: '12 Augusta Canyon Way',
  area: 'Southern Highlands Golf Club',
  beds: 5,
  baths: 5,
  sqft: '4,600',
  image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$725,000',
  address: '4891 Montecito Dr',
  area: 'Southern Highlands',
  beds: 4,
  baths: 3,
  sqft: '2,850',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 3,
  price: '$1,150,000',
  address: '11 Promontory Pointe Ln',
  area: 'Southern Highlands',
  beds: 4,
  baths: 4.5,
  sqft: '3,900',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
}];
export function SouthernHighlandsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-1408">
      <SEOHead title="Southern Highlands Real Estate & Luxury Homes | Jenny Gong" description="Discover exclusive gated estates and luxury homes in Southern Highlands, Las Vegas. Featuring championship golf and Mediterranean-inspired living." keywords="Southern Highlands real estate, Southern Highlands homes for sale, Southern Highlands Golf Club, luxury homes Southern Highlands Las Vegas" canonicalUrl="https://jennygong.com/southern-highlands" ogImage="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop" data-id="element-1409" />
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-1410">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop")'
      }} data-id="element-1411">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-1412"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-1413"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-1414">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-1415">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-1416">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-1417">
              Southern Highlands Homes for Sale
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-1418">
              An exclusive gated enclave featuring championship golf and
              Mediterranean-inspired luxury estates.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview & Stats */}
      <section className="py-24 px-6 bg-white" data-id="element-1419">
        <div className="max-w-7xl mx-auto" data-id="element-1420">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1421">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1422">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1423">
                Where Elegance Meets{' '}
                <span className="italic text-gold" data-id="element-1424">Exclusivity</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1425">
                <p data-id="element-1426">
                  Southern Highlands is one of the most prestigious
                  master-planned communities in Las Vegas, located in the
                  southern tip of the valley along the I-15 corridor. Developed
                  in the late 1990s, this guard-gated community spans over 2,300
                  acres and is home to approximately 7,000 residents who enjoy
                  an unparalleled level of privacy and luxury.
                </p>
                <p data-id="element-1427">
                  The community is anchored by the Southern Highlands Golf Club,
                  a stunning 18-hole championship course designed by Robert
                  Trent Jones Sr. and Jr. Homes range from elegant $500K
                  residences to breathtaking $5M+ custom estates with sweeping
                  mountain and city views — comparable to the finest estates in{' '}
                  <Link to="/summerlin" className="text-gold hover:underline" data-id="element-1428">
                    Summerlin
                  </Link>{' '}
                  and{' '}
                  <Link to="/macdonald-highlands" className="text-gold hover:underline" data-id="element-1429">
                    MacDonald Highlands
                  </Link>
                  .
                </p>
                <p data-id="element-1430">
                  Southern Highlands is known for its Mediterranean-inspired
                  architecture, lush landscaping, and resort-style amenities
                  that create a country club lifestyle without ever leaving
                  home.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-1431">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1432">
                <Trophy className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1433" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1434">18</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1435">
                  Hole Golf Course
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1436">
                <Shield className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1437" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1438">24/7</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1439">
                  Guard Gated
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1440">
                <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1441" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1442">20m</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1443">
                  To The Strip
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1444">
                <Star className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1445" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1446">Top</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1447">
                  Luxury Community
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Golf & Country Club */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1448">
        <div className="max-w-7xl mx-auto" data-id="element-1449">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1450">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-1451">
              <div className="relative h-[500px]" data-id="element-1452">
                <img src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=2940&auto=format&fit=crop" alt="Golf Course" className="w-full h-full object-cover" data-id="element-1453" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1454"></div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-1455">
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1456">
                The Centerpiece
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1457">
                Southern Highlands Golf Club
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1458">
                <p data-id="element-1459">
                  The crown jewel of the community, Southern Highlands Golf Club
                  features an 18-hole championship course designed by the
                  legendary Robert Trent Jones Sr. and Jr. The course winds
                  through the natural desert terrain with dramatic elevation
                  changes and stunning mountain views.
                </p>
                <p data-id="element-1460">
                  Members enjoy a 74,000 square foot Mediterranean-style
                  clubhouse with fine dining, a world-class spa, tennis courts,
                  fitness center, and resort-style swimming pools. It's a
                  lifestyle destination that rivals the finest country clubs in
                  the nation.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lifestyle & Amenities */}
      <section className="py-24 px-6 bg-white" data-id="element-1461">
        <div className="max-w-7xl mx-auto" data-id="element-1462">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1463">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1464">
              Resort-Style Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1465">
              Lifestyle & Amenities
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1466">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1467">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1468">
                <img src="https://images.unsplash.com/photo-1540539234-c14a20fb7c7b?q=80&w=2940&auto=format&fit=crop" alt="Spa" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1469" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1470">
                World-Class Spa & Fitness
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1471">
                A full-service spa, state-of-the-art fitness center, and
                wellness programs designed for the ultimate in relaxation and
                health.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1472">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1473">
                <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2940&auto=format&fit=crop" alt="Fine Dining" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1474" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1475">
                Fine Dining
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1476">
                Multiple dining venues within the clubhouse offering everything
                from casual poolside fare to elegant multi-course dinners.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-1477">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1478">
                <img src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=2940&auto=format&fit=crop" alt="Resort Pool" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1479" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1480">
                Resort-Style Pools
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1481">
                Multiple swimming pools, cabanas, and outdoor lounging areas
                perfect for enjoying 300+ days of Las Vegas sunshine.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nearby Attractions */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1482">
        <div className="max-w-7xl mx-auto" data-id="element-1483">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1484">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-8" data-id="element-1485">
              <div data-id="element-1486">
                <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1487">
                  Perfectly Located
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1488">
                  Nearby Attractions
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1489">
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1490">
                  <ShoppingBag className="w-6 h-6 text-gold mb-3" data-id="element-1491" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1492">
                    The District at Green Valley
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1493">
                    Upscale shopping and dining just minutes away with boutiques
                    and restaurants.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1494">
                  <Mountain className="w-6 h-6 text-gold mb-3" data-id="element-1495" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1496">
                    Sloan Canyon
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1497">
                    National Conservation Area with ancient petroglyphs and
                    stunning desert hiking trails.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1498">
                  <Utensils className="w-6 h-6 text-gold mb-3" data-id="element-1499" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1500">
                    M Resort Spa Casino
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1501">
                    A luxury boutique resort with fine dining, entertainment,
                    and a world-class spa.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1502">
                  <MapPin className="w-6 h-6 text-gold mb-3" data-id="element-1503" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1504">
                    Las Vegas Premium Outlets
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1505">
                    South location offering designer brands at outlet prices,
                    just a short drive away.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1506">
              <div className="relative h-[600px]" data-id="element-1507">
                <img src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2940&auto=format&fit=crop" alt="Mountain Views" className="w-full h-full object-cover" data-id="element-1508" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1509"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schools */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-1510">
        <div className="max-w-7xl mx-auto" data-id="element-1511">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1512">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1513" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1514">
              Education
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1515">
              Top-Rated Schools
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-1516">
              Southern Highlands is zoned for some of Clark County's
              highest-performing schools and offers immediate access to elite
              private academies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1517">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1518">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1519">
                Elementary & Middle
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1520">
                <li data-id="element-1521">• Evelyn Stuckey Elementary</li>
                <li data-id="element-1522">• Aldeane Comito Ries Elementary</li>
                <li data-id="element-1523">• Charles & Phyllis Frias Elementary</li>
                <li data-id="element-1524">• Lois & Jerry Tarkanian Middle</li>
                <li data-id="element-1525">• Desert Oasis Middle School</li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1526">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1527">
                High Schools
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1528">
                <li data-id="element-1529">• Desert Oasis High School</li>
                <li data-id="element-1530">• Liberty High School</li>
                <li data-id="element-1531">• Sierra Vista High School</li>
                <li className="pt-4 italic text-xs text-gold" data-id="element-1532">
                  Desert Oasis features specialized magnet programs and strong
                  athletics.
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1533">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1534">
                Private & Charter
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1535">
                <li data-id="element-1536">• Southern Highlands Preparatory School</li>
                <li data-id="element-1537">• Bishop Gorman High School (Elite Catholic Prep)</li>
                <li data-id="element-1538">• Pinecrest Academy</li>
                <li data-id="element-1539">• Doral Academy</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parks & Recreation */}
      <section className="py-24 px-6 bg-white" data-id="element-1540">
        <div className="max-w-7xl mx-auto" data-id="element-1541">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1542">
            <TreePine className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1543" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1544">
              Outdoor Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1545">
              Parks & Recreation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1546">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1547">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1548">
                <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=2940&auto=format&fit=crop" alt="Goett Family Park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1549" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1550">
                Goett Family Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1551">
                A beautiful community park featuring a splash pad, covered
                picnic areas, basketball courts, and expansive green spaces
                perfect for family gatherings.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1552">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1553">
                <img src="https://images.unsplash.com/photo-1584467735815-f778f274e296?q=80&w=2787&auto=format&fit=crop" alt="Somerset Hills Park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1554" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1555">
                Somerset Hills Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1556">
                Features lighted tennis courts, a water play area, gazebos, and
                a dedicated dog park, making it a central hub for active
                residents.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-1557">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1558">
                <img src="https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?q=80&w=2948&auto=format&fit=crop" alt="Jimmy Pettyjohn Jr. Park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1559" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1560">
                Jimmy Pettyjohn Jr. Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1561">
                A scenic park offering walking trails, play structures, and
                peaceful seating areas integrated beautifully into the community
                landscape.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="group" data-id="element-1562">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1563">
                <img src="https://images.unsplash.com/photo-1472214103451-9374bd1c798e?q=80&w=2940&auto=format&fit=crop" alt="Trail System" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1564" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1565">
                Community Trail System
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1566">
                Miles of paved, tree-lined walking and biking trails weave
                throughout Southern Highlands, connecting neighborhoods, parks,
                and the country club.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shopping & Dining */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1567">
        <div className="max-w-7xl mx-auto" data-id="element-1568">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1569">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-1570">
              <div className="relative h-[600px]" data-id="element-1571">
                <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2874&auto=format&fit=crop" alt="Dining" className="w-full h-full object-cover" data-id="element-1572" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1573"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-1574">
              <ShoppingBag className="w-10 h-10 text-gold mb-6" data-id="element-1575" />
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1576">
                Lifestyle & Culinary
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-8" data-id="element-1577">
                Shopping & Dining
              </h2>

              <div className="space-y-8" data-id="element-1578">
                <div data-id="element-1579">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1580">
                    <ShoppingBag className="w-5 h-5 text-gold" data-id="element-1581" />
                    Southern Highlands Marketplace
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1582">
                    Conveniently located at the community entrance, offering
                    grocery stores, boutique fitness studios, salons, and daily
                    conveniences.
                  </p>
                </div>

                <div data-id="element-1583">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1584">
                    <Utensils className="w-5 h-5 text-gold" data-id="element-1585" />
                    Local Dining Scene
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1586">
                    Features a mix of casual eateries, coffee shops, and upscale
                    dining options, including the acclaimed Spaghetty Western
                    and Off The Vine.
                  </p>
                </div>

                <div data-id="element-1587">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1588">
                    <ShoppingBag className="w-5 h-5 text-gold" data-id="element-1589" />
                    Nearby Retail Hubs
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1590">
                    Just minutes from the massive Blue Diamond retail corridor
                    and Town Square Las Vegas, an open-air shopping and
                    entertainment center.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="py-24 px-6 bg-white" data-id="element-1591">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1592">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1593">
            <Heart className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1594" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1595">
              Wellness
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1596">
              World-Class Healthcare
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-1597">
              Southern Highlands residents enjoy proximity to excellent medical
              facilities. The community is just minutes from{' '}
              <strong data-id="element-1598">Southern Hills Hospital & Medical Center</strong> and{' '}
              <strong data-id="element-1599">St. Rose Dominican Hospital - Siena Campus</strong>.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1600">
              The immediate area is also well-served by numerous specialized
              clinics, pediatricians, dental offices, and urgent care centers
              located right within the community's commercial plazas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transportation */}
      <section className="py-24 bg-charcoal px-6 text-white" data-id="element-1601">
        <div className="max-w-7xl mx-auto" data-id="element-1602">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1603">
            <MapPin className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1604" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1605">
              Connectivity
            </span>
            <h2 className="text-4xl font-serif mb-6" data-id="element-1606">
              Transportation & Access
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1607">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1608">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1609" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1610">I-15 Access</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1611">
                Direct, immediate access to I-15 via the St. Rose Parkway
                interchange, making commutes effortless.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1612">
              <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1613" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1614">St. Rose Parkway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1615">
                A major corridor connecting Southern Highlands directly to
                Henderson and the 215 Beltway.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1616">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1617" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1618">Harry Reid Airport</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1619">
                An incredibly convenient 10-15 minute drive straight up I-15,
                perfect for frequent flyers.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1620">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1621" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1622">The Strip</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1623">
                Just 15 minutes away, offering world-class entertainment while
                maintaining suburban privacy.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <RelatedCommunities currentCommunity="Southern Highlands" data-id="element-1624" />

      <ListingsComingSoon area="Southern Highlands" data-id="element-1625" />

      <ContactCTA data-id="element-1626" />
    </main>;
}